import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  hero: {
    titulo: { es: "¡Gracias por viajar con nosotros!" },
    lede: {
      es: "Tu opinión es fundamental para nosotros. Por favor, tómate un momento para responder estas preguntas sobre tu experiencia de viaje.",
      en: "Your feedback is essential to us. Please take a moment to answer these questions about your travel experience.",
      fr: "Vos commentaires sont essentiels pour nous. Veuillez prendre un moment pour répondre à ces questions sur votre expérience de voyage.",
    },
  },
  campos: {
    nombre: { es: "Nombre", en: "Name", fr: "Nom" },
    empresa: { es: "Empresa", en: "Company", fr: "Entreprise" },
    email: { es: "Correo electrónico de contacto", en: "Contact email", fr: "Adresse e-mail de contact" },
    general: { es: "¿Cómo calificarías tu experiencia general con nuestro servicio?", en: "How would you rate your overall experience with our service?", fr: "Comment évalueriez-vous votre expérience globale avec notre service ?" },
    comunicacion: { es: "¿Cómo evaluarías la comunicación y la información proporcionada antes y durante el viaje?", en: "How would you evaluate the communication and information provided before and during the trip?", fr: "Comment évalueriez-vous la communication et les informations fournies avant et pendant le voyage ?" },
    eficiencia: { es: "¿Cómo evaluarías la eficiencia de nuestro equipo en la gestión de tu viaje?", en: "How would you evaluate the efficiency of our team in managing your trip?", fr: "Comment évalueriez-vous l'efficacité de notre équipe dans la gestion de votre voyage ?" },
    recomienda: { es: "¿Recomendarías nuestros servicios a amigos, familiares o colegas?", en: "Would you recommend our services to friends, family, or colleagues?", fr: "Recommanderiez-vous nos services à vos amis, votre famille ou vos collègues ?" },
    comentarios: { es: "Comentarios", en: "Comments", fr: "Commentaires" },
  },
  gracias: { es: "Gracias por confiar en PD Travel", en: "Thank you for trusting PD Travel", fr: "Merci de faire confiance à PD Travel" },
  cerrada: { es: "Esta encuesta ya no está disponible", en: "This survey is no longer available", fr: "Ce sondage n'est plus disponible" },
};

const EXTRA = {
  si: { es: "Sí", en: "Yes", fr: "Oui" },
  no: { es: "No", en: "No", fr: "Non" },
  enviar: { es: "Enviar respuestas", en: "Submit", fr: "Envoyer" },
  enviando: { es: "Enviando…", en: "Submitting…", fr: "Envoi…" },
  requerido: { es: "Este campo es obligatorio", en: "This field is required", fr: "Ce champ est obligatoire" },
  error: { es: "No se pudo enviar. Revisa los campos e intenta de nuevo.", en: "Could not submit. Please try again.", fr: "Échec de l'envoi. Veuillez réessayer." },
};

const langsOf = (cfg) => ["es", ...(cfg?.mostrar_en ? ["en"] : []), ...(cfg?.mostrar_fr ? ["fr"] : [])];

const Stacked = ({ obj, langs, className = "", req = false }) => (
  <span className={className}>
    <span className="block">{obj.es}{req && <span className="text-red-500"> *</span>}</span>
    {langs.includes("en") && obj.en && <span className="block italic text-[0.92em] opacity-70 mt-0.5">{obj.en}</span>}
    {langs.includes("fr") && obj.fr && <span className="block italic text-[0.92em] opacity-70 mt-0.5">{obj.fr}</span>}
  </span>
);

const Star = ({ filled }) => (
  <svg viewBox="0 0 24 24" className="w-8 h-8 sm:w-9 sm:h-9" fill={filled ? "var(--brand-accent)" : "#e4e4e7"}>
    <path d="M12 2l2.94 6.26 6.87.87-5.06 4.73 1.31 6.79L12 17.27l-6.06 3.38 1.31-6.79L2.19 9.13l6.87-.87L12 2z" />
  </svg>
);

const Rating = ({ value, onChange, testid }) => (
  <div className="flex gap-1.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" data-testid={`${testid}-${n}`} onClick={() => onChange(n)}
        className="transition-transform hover:scale-110 focus:outline-none" aria-label={`${n} de 5`}>
        <Star filled={value >= n} />
      </button>
    ))}
  </div>
);

export default function EncuestaPublica() {
  const { slug } = useParams();
  const [cfg, setCfg] = useState(null);
  const [state, setState] = useState("loading"); // loading | form | cerrada | notfound | success
  const [form, setForm] = useState({ nombre: "", empresa: "", email: "", experiencia_general: 0, comunicacion: 0, eficiencia: 0, recomienda: null, comentarios: "", website: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/encuestas/public/${slug}`, { withCredentials: false });
        setCfg(data);
        if (!data.activo) { setState("cerrada"); return; }
        setState("form");
        const key = `pd_visit_${slug}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          axios.post(`${API}/encuestas/public/${slug}/visita`, {}, { withCredentials: false }).catch(() => {});
        }
      } catch {
        setState("notfound");
      }
    })();
  }, [slug]);

  const langs = langsOf(cfg);
  const pal = cfg?.paleta || { base: "#0037ff", acento: "#0037ff", oscuro: "#0a1230", suave: "#e8ecff" };
  const vars = { "--brand": pal.base, "--brand-accent": pal.acento, "--brand-deep": pal.oscuro, "--brand-soft": pal.suave };
  const logoUrl = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : null);

  const validate = () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = true;
    if (!form.empresa.trim()) e.empresa = true;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) e.email = true;
    ["experiencia_general", "comunicacion", "eficiencia"].forEach((k) => { if (!form[k]) e[k] = true; });
    if (form.recomienda === null) e.recomienda = true;
    if (!form.comentarios.trim()) e.comentarios = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/encuestas/public/${slug}/responder`, {
        ...form,
        nombre: form.nombre.trim(), empresa: form.empresa.trim(), email: form.email.trim().toLowerCase(),
        comentarios: form.comentarios.trim(),
      }, { withCredentials: false });
      setState("success");
      window.scrollTo(0, 0);
    } catch (err) {
      if (err?.response?.status === 409) setState("cerrada");
      else setErrors((p) => ({ ...p, _global: true }));
    } finally {
      setSending(false);
    }
  };

  if (state === "loading") {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="w-8 h-8 border-4 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" /></div>;
  }

  if (state === "notfound" || state === "cerrada") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-6" style={vars} data-testid="eq-cerrada-screen">
        <div className="max-w-md w-full bg-white rounded-2xl border border-zinc-200 p-10 text-center shadow-sm">
          <div className="text-4xl mb-4">✉️</div>
          <Stacked obj={T.cerrada} langs={state === "cerrada" ? langs : ["es", "en"]} className="text-lg font-semibold text-zinc-800" />
          <p className="text-xs text-zinc-400 mt-6">PD TRAVEL</p>
        </div>
      </div>
    );
  }

  if (state === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ ...vars, background: "var(--brand-soft)" }} data-testid="eq-gracias-screen">
        <div className="max-w-md w-full bg-white rounded-2xl border border-zinc-200 p-10 text-center shadow-lg">
          <div className="w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: "var(--brand)" }}>
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="white" strokeWidth="3"><path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <Stacked obj={T.gracias} langs={langs} className="text-xl font-bold text-zinc-900" />
          <p className="text-xs text-zinc-400 mt-8">Powered by <span className="font-bold text-zinc-600">PD TRAVEL</span></p>
        </div>
      </div>
    );
  }

  const fieldCls = (k) => `w-full rounded-xl border px-4 py-3 text-[15px] outline-none transition-colors bg-white focus:border-[var(--brand)] ${errors[k] ? "border-red-400" : "border-zinc-200"}`;

  return (
    <div className="min-h-screen bg-zinc-50" style={vars} data-testid="eq-form-page">
      {/* Header oscuro con logos */}
      <header className="py-6 px-6" style={{ background: "var(--brand-deep)" }}>
        <div className="max-w-2xl mx-auto flex items-center justify-center gap-8 flex-wrap">
          {logoUrl(cfg.logo_cliente_url) && <img src={logoUrl(cfg.logo_cliente_url)} alt={cfg.cliente} className="h-12 sm:h-14 object-contain" data-testid="eq-logo-cliente" />}
          {logoUrl(cfg.logo_evento_url) && <img src={logoUrl(cfg.logo_evento_url)} alt="" className="h-12 sm:h-14 object-contain" data-testid="eq-logo-evento" />}
          {!cfg.logo_cliente_url && !cfg.logo_evento_url && <span className="text-white font-bold tracking-widest text-lg">{cfg.cliente?.toUpperCase()}</span>}
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 pt-10 pb-6 text-center">
        <div className="max-w-2xl mx-auto">
          <span className="inline-block text-[11px] font-bold tracking-[0.18em] uppercase px-4 py-1.5 rounded-full mb-5" style={{ background: "var(--brand-soft)", color: "var(--brand-deep)" }} data-testid="eq-eyebrow">
            {cfg.nombre}
          </span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 leading-tight" style={{ fontFamily: "'Mulish', sans-serif" }}>{T.hero.titulo.es}</h1>
          {cfg.subtitulo && <p className="mt-2 text-lg font-semibold" style={{ color: "var(--brand)" }} data-testid="eq-subtitulo">{cfg.subtitulo}</p>}
          <Stacked obj={T.hero.lede} langs={langs} className="block mt-4 text-[15px] text-zinc-500 leading-relaxed" />
        </div>
      </section>

      {/* Formulario */}
      <form onSubmit={submit} className="px-4 sm:px-6 pb-16" noValidate>
        <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-zinc-200 shadow-sm p-6 sm:p-10 space-y-8">
          {/* honeypot */}
          <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
            className="absolute -left-[9999px] opacity-0 h-0 w-0" tabIndex={-1} autoComplete="off" aria-hidden="true" />

          {[["nombre", "text"], ["empresa", "text"], ["email", "email"]].map(([k, type]) => (
            <div key={k}>
              <label className="block mb-2 font-semibold text-zinc-800 text-[15px]">
                <Stacked obj={T.campos[k]} langs={langs} /><span className="text-red-500"> *</span>
              </label>
              <input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className={fieldCls(k)} maxLength={120} data-testid={`eq-input-${k}`} />
              {errors[k] && <p className="text-xs text-red-500 mt-1">{EXTRA.requerido.es}</p>}
            </div>
          ))}

          {[["experiencia_general", "general"], ["comunicacion", "comunicacion"], ["eficiencia", "eficiencia"]].map(([k, tk]) => (
            <div key={k}>
              <label className="block mb-3 font-semibold text-zinc-800 text-[15px]">
                <Stacked obj={T.campos[tk]} langs={langs} req />
              </label>
              <Rating value={form[k]} onChange={(v) => setForm({ ...form, [k]: v })} testid={`eq-rating-${k}`} />
              {errors[k] && <p className="text-xs text-red-500 mt-1">{EXTRA.requerido.es}</p>}
            </div>
          ))}

          <div>
            <label className="block mb-3 font-semibold text-zinc-800 text-[15px]">
              <Stacked obj={T.campos.recomienda} langs={langs} req />
            </label>
            <div className="flex gap-3">
              {[[true, "si"], [false, "no"]].map(([val, key]) => (
                <button key={key} type="button" data-testid={`eq-recomienda-${key}`}
                  onClick={() => setForm({ ...form, recomienda: val })}
                  className="px-8 py-3 rounded-xl border font-semibold text-[15px] transition-all"
                  style={form.recomienda === val
                    ? { background: "var(--brand-accent)", borderColor: "var(--brand-accent)", color: "#fff" }
                    : { background: "#fff", borderColor: "#e4e4e7", color: "#3f3f46" }}>
                  <Stacked obj={EXTRA[key]} langs={langs} />
                </button>
              ))}
            </div>
            {errors.recomienda && <p className="text-xs text-red-500 mt-1">{EXTRA.requerido.es}</p>}
          </div>

          <div>
            <label className="block mb-2 font-semibold text-zinc-800 text-[15px]">
              <Stacked obj={T.campos.comentarios} langs={langs} req />
            </label>
            <textarea value={form.comentarios} onChange={(e) => setForm({ ...form, comentarios: e.target.value })}
              rows={4} maxLength={2000} className={fieldCls("comentarios")} data-testid="eq-input-comentarios" />
            {errors.comentarios && <p className="text-xs text-red-500 mt-1">{EXTRA.requerido.es}</p>}
          </div>

          {errors._global && <p className="text-sm text-red-500 text-center">{EXTRA.error.es}</p>}

          <button type="submit" disabled={sending} data-testid="eq-submit-btn"
            className="w-full py-4 rounded-xl text-white font-bold text-base transition-opacity disabled:opacity-60 hover:opacity-90"
            style={{ background: "var(--brand-accent)" }}>
            <Stacked obj={sending ? EXTRA.enviando : EXTRA.enviar} langs={langs} />
          </button>
        </div>
        <p className="text-center text-xs text-zinc-400 mt-6">Powered by <span className="font-bold text-zinc-500">PD TRAVEL</span></p>
      </form>
    </div>
  );
}
